import React, {useEffect, useCallback} from 'react';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import './storepage.scss';
import {Filters} from './components/filters/filters';
import filtersStore from './components/filters/filters-store';
import s from './storepage-datasource';
import ReactPaginate from 'react-paginate';
import {Portal} from '../util';

declare var __store_h1: string;
declare var __store_h1_subText: string;
declare var __is_my_frames: boolean;
declare var __fast_ship_text: string;
declare var __fast_ship_text_mobile: string;
declare var __store_has_h1_subText: boolean;
declare var __fastShipIds: number[];
declare var _msrp_ab: boolean;

export const StorePageComponent: React.FC = observer(() => {
    useEffect(() => {
        filtersStore.getData();
    }, []);

    const htmlDecode = useCallback((input: string): string => {
        const doc = new DOMParser().parseFromString(input, 'text/html');

        return doc.documentElement.textContent || '';
    }, []);

    const toggleMenu = useCallback(
        action(() => {
            const menu = document.getElementById('store-filters-container') as HTMLElement;
            const display = getComputedStyle(menu).display;

            if (display === 'none') {
                menu.style.display = 'block';
                menu.classList.add('filters-visible');
            } else {
                menu.style.display = 'none';
                menu.classList.remove('filters-visible');
            }
        }),
        [],
    );

    const onPageClick = useCallback(
        action(async (selected: number) => {
            await filtersStore.updateFilter('pageNumber', selected + 1);
            s.getFilterOptions();
            window.scrollTo({top: 0, behavior: 'smooth'});
        }),
        [],
    );

    const formatCurrency = useCallback((value: number): string => {
        // Use Intl.NumberFormat for proper currency formatting
        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2, // removes .00 if unnecessary
            maximumFractionDigits: 2, // ensures cents if needed
        }).format(value);

        return formatted.replace('.00', '');
    }, []);

    const getDiscountedPrice = useCallback(
        action((_price: number): string => {
            let price_discount = _price;

            if (_price > 400) {
                price_discount -= 20;
            } else if (_price > 300) {
                price_discount -= 15;
            } else if (_price > 200) {
                price_discount -= 10;
            } else {
                price_discount = Math.round(_price - (_price * 0.05));
            }

            return formatCurrency(price_discount);
        }),
        [formatCurrency],
    );

    return (
        <div
            id="store-special-container"
            className={filtersStore.loading ? 'loading' : ''}
        >
            <Filters />

            <div id="products">
                <div className="row">
                    <div className="col text-center mt-3 mb-2">
                        {__store_h1 && <h1 dangerouslySetInnerHTML={{__html: htmlDecode(__store_h1)}}></h1>}
                        {__store_has_h1_subText && (
                            <div
                                className="sub-h1"
                                dangerouslySetInnerHTML={{__html: htmlDecode(__store_h1_subText)}}
                            ></div>
                        )}
                    </div>
                </div>
                <div
                    className="text-center"
                    onClick={toggleMenu}
                >
                    <div
                        id="menu-toggle"
                        role="button"
                        aria-label="Open Filter Menu"
                    >
                        Filter{' '}
                        <span style={{marginLeft: 6}}>
                            <i className="fas fa-sliders"></i>
                        </span>
                    </div>
                </div>
                {s.hasProducts && (
                    <>
                        <div id="products-container">
                            {s.products.hits.hits.map(p => (
                                <div className="flex-item">
                                    <a
                                        href={p._source.fsi.URL}
                                        aria-label={`View Product: ${p._source.Name} Item # ${p._source.ItemID}`}
                                    >
                                        <div className="prod-image">
                                            {!__is_my_frames && !p._source.hideAisleA && Portal?.PortalID !== 3 && __fastShipIds.includes(p._source.ProductID) && (
                                                <div
                                                    className="store-highlight-container"
                                                    data-product={p._source.ProductID}
                                                >
                                                    <span className={`store-highlight fast-shipping-${Portal?.PortalID}`}>
                                                        <span className="d-md-none">
                                                            {__fast_ship_text_mobile} <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}></i>
                                                        </span>
                                                        <span className="d-none d-sm-none d-md-inline-block d-lg-inline-block d-xl-inline-block">
                                                            {__fast_ship_text} <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}></i>
                                                        </span>
                                                    </span>
                                                </div>
                                            )}

                                            {p._source.is_tassel_cord && (
                                                <div
                                                    className="store-highlight-container"
                                                    data-product={p._source.ProductID}
                                                >
                                                    <span className="store-highlight store-highlight-cord-lg">
                                                        <span>Also Holds An Honor Cord</span>
                                                    </span>
                                                    <span className="store-highlight store-highlight-cord-sm">
                                                        <span>Also Holds Honor Cord</span>
                                                    </span>
                                                </div>
                                            )}

                                            <div className="image-container">
                                                {p._source.CustomizationMethod < 2 ? (
                                                    <picture id={`prod-img-${p._source.ProductID}`}>
                                                        <source
                                                            type="image/webp"
                                                            srcSet={
                                                                p._source.Image_Full
                                                                    ? `https://${Portal?.CDNDomain}/images/products/full/${p._source.Image_Full.toLowerCase().replace('.png', '.webp').replace('.jpg', '.webp')}`
                                                                    : `https://${Portal?.CDNDomain}/images/products/thumb/${p._source.Image_Thumb.toLowerCase()}`
                                                            }
                                                        />
                                                        <img
                                                            src={p._source.Image_Full ? `https://${Portal?.CDNDomain}/images/products/full/${p._source.Image_Full.toLowerCase()}` : `https://${Portal?.CDNDomain}/images/products/thumb/${p._source.Image_Thumb.toLowerCase()}`}
                                                            style={{maxHeight: 250}}
                                                            loading="lazy"
                                                            alt={p._source.Name}
                                                        />
                                                    </picture>
                                                ) : (
                                                    <picture id={`prod-img-${p._source.ProductID}`}>
                                                        <source
                                                            type="image/webp"
                                                            srcSet={`https://${Portal?.CDNDomain}/images/products/gen/products/${p._source.ImageItemID?.toLowerCase()}_${p._source.ProductID}_308_308.webp`}
                                                        />
                                                        <img
                                                            src={`https://${Portal?.CDNDomain}/images/products/gen/products/${p._source.ImageItemID?.toLowerCase()}_${p._source.ProductID}_308_308.png`}
                                                            style={{maxHeight: 250}}
                                                            loading="lazy"
                                                            alt={p._source.Name}
                                                        />
                                                    </picture>
                                                )}

                                                {(p._source.ProductTypeID == 83 || p._source.ProductTypeID == 84 || (p._source.ProductTypeID == 87 && p._source.ProductID > 90000)) && Portal?.PortalID === 0 && (
                                                    <div className="badge">
                                                        <span>New</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {p._source.fsi.GradSchoolID > 0 && p._source.fsi.GSName && (
                                            <div className="name">
                                                {p._source.fsi.InstName} - {p._source.fsi.GSName} - {p._source.Name}
                                            </div>
                                        )}
                                        {(!p._source.fsi.GradSchoolID || p._source.fsi.GradSchoolID < 1) && (
                                            <div className="name">
                                                {p._source.fsi.InstName} - {p._source.Name}
                                            </div>
                                        )}

                                        {__is_my_frames && (
                                            <div className="price">
                                                <div>
                                                    Wholesale:{' '}
                                                    <span
                                                        aria-label="MyFrames Price"
                                                        className="price"
                                                    >
                                                        ${p._source.fsi.Price_Wholesale}
                                                    </span>
                                                </div>
                                                <div>
                                                    Retail:{' '}
                                                    <span
                                                        aria-label="MyFrames Price"
                                                        className="price"
                                                    >
                                                        ${p._source.fsi.Price_Retail}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {!__is_my_frames && !_msrp_ab && (
                                            <div className="price">
                                                <div>
                                                    <span
                                                        aria-label="Price"
                                                        className="price"
                                                    >
                                                        ${p._source.fsi.priceMSRP}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {!__is_my_frames && _msrp_ab && (
                                            <div className="price">
                                                <div>
                                                    <span
                                                        aria-label="Price"
                                                        className="price-discount"
                                                    >
                                                        {getDiscountedPrice(p._source.fsi.priceMSRPDec)}
                                                    </span>
                                                    <span
                                                        aria-label="Price"
                                                        className="price-msrp"
                                                    >
                                                        ${p._source.fsi.priceMSRP}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="sku">Item # {p._source.ItemID}</div>

                                        {p._source.reviewCount > 0 && !__is_my_frames && Portal?.PortalID !== 3 && s.allow_reviews && (
                                            <div className="rating">
                                                <div className="rating-inner">
                                                    <div className="stars">
                                                        <span>
                                                            <i
                                                                className={`fa fa-star ${p._source.reviewScore > 0 ? ' checked' : ''}`}
                                                                role="presentation"
                                                            ></i>
                                                        </span>
                                                        <span>
                                                            <i
                                                                className={`fa fa-star ${p._source.reviewScore > 1 ? ' checked' : ''}`}
                                                                role="presentation"
                                                            ></i>
                                                        </span>
                                                        <span>
                                                            <i
                                                                className={`fa fa-star ${p._source.reviewScore > 2 ? ' checked' : ''}`}
                                                                role="presentation"
                                                            ></i>
                                                        </span>
                                                        <span>
                                                            <i
                                                                className={`fa fa-star ${p._source.reviewScore > 3 ? ' checked' : ''}`}
                                                                role="presentation"
                                                            ></i>
                                                        </span>
                                                        <span>
                                                            <i
                                                                className={`fa fa-star ${p._source.reviewScore > 4 ? ' checked' : ''}`}
                                                                role="presentation"
                                                            ></i>
                                                        </span>
                                                    </div>
                                                    <span className="rating-desc">({p._source.reviewCount.toString()})</span>
                                                </div>
                                            </div>
                                        )}
                                    </a>

                                    <hr
                                        aria-hidden="true"
                                        className="show-small"
                                    />
                                </div>
                            ))}
                        </div>

                        {s.products.hits.total.value > 100 && (
                            <nav
                                role="navigation"
                                aria-label="Pagination"
                            >
                                <ReactPaginate
                                    previousLabel="Prev"
                                    nextLabel="Next"
                                    breakLabel="..."
                                    containerClassName={`pagination${filtersStore.loading ? ' loading' : ''}`}
                                    activeClassName="active"
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={3}
                                    forcePage={filtersStore.filters.pageNumber - 1}
                                    pageCount={Math.ceil(s.products.hits.total.value / 100)}
                                    onPageChange={e => {
                                        onPageClick(e.selected);
                                        setTimeout(s.stripPaginationAriaAttributes, 150);
                                    }}
                                />
                            </nav>
                        )}

                        <div className="col d-none d-md-block">
                            <hr />
                        </div>
                    </>
                )}

                {!s.hasProducts && !filtersStore.loading && <h2 className="text-center w-100">{Portal?.PortalID === 3 ? 'Please try another search so we can help you frame what matters most.' : 'No results found, please try another search.'}</h2>}
            </div>
        </div>
    );
});
